'use client';
import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import { contactService, type ContactMessage } from '@/lib/services/contactService';
import FormErrorBoundary from '@/components/common/FormErrorBoundary';
import Toast from '@/components/common/Toast';
import ConfirmationModal from '@/components/common/ConfirmationModal';

interface FormData {
  name: string;
  email: string;
  message: string;
}

interface FormErrors {
  name?: string;
  email?: string;
  message?: string;
}

const ContactFormSection = () => {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    message: '',
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string>('');
  const [showToast, setShowToast] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [submittedData, setSubmittedData] = useState<FormData | null>(null);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateField = (name: string, value: string): string => {
    switch (name) {
      case 'name':
        if (!value.trim()) return 'Name is required';
        if (value.trim().length < 2) return 'Name must be at least 2 characters';
        return '';
      case 'email':
        if (!value.trim()) return 'Email is required';
        if (!validateEmail(value)) return 'Please enter a valid email address';
        return '';
      case 'message':
        if (!value.trim()) return 'Message is required';
        if (value.trim().length < 10) return 'Message must be at least 10 characters';
        return '';
      default:
        return '';
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });

    // Clear error for this field when user starts typing
    if (errors[name as keyof FormErrors]) {
      setErrors({
        ...errors,
        [name]: '',
      });
    }
    setSubmitError('');
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    const error = validateField(name, value);
    if (error) {
      setErrors({
        ...errors,
        [name]: error,
      });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    
    const nameError = validateField('name', formData.name);
    if (nameError) newErrors.name = nameError;

    const emailError = validateField('email', formData.email);
    if (emailError) newErrors.email = emailError;

    const messageError = validateField('message', formData.message);
    if (messageError) newErrors.message = messageError;

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError('');

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const messageData: ContactMessage = {
        name: formData.name,
        email: formData.email,
        message: formData.message,
      };

      const success = await contactService.submitMessage(messageData);

      if (success) {
        // Send customer confirmation email
        try {
          await fetch('/api/send-customer-confirmation', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              to: formData.email,
              name: formData.name,
              type: 'contact',
            }),
          });
        } catch (emailError) {
          console.log('Customer confirmation email failed:', emailError);
        }

        // Store submitted data for modal
        setSubmittedData({ ...formData });
        
        // Show success feedback
        setShowToast(true);
        setShowModal(true);
        setIsSuccess(true);
        
        // Reset form
        setFormData({
          name: '',
          email: '',
          message: '',
        });
        setErrors({});
      } else {
        setSubmitError('Failed to send message. Please try again or contact us via WhatsApp.');
      }
    } catch (error) {
      console.error('Contact form submission failed:', error);
      setSubmitError('An error occurred while sending your message. Please try again or contact us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-16 sm:py-24 bg-white">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 md:px-10">
        {/* Section Header */}
        <div className="reveal text-center mb-8 sm:mb-12">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold font-montserrat text-foreground mb-4">
            Send Us a Message
          </h2>
          <p className="text-sm sm:text-base text-muted-foreground">
            Fill out the form below and we'll get back to you as soon as possible.
          </p>
        </div>

        {/* Form */}
        <div className="reveal container-card p-6 sm:p-8 md:p-12">
          <FormErrorBoundary formName="Contact Form">
            {/* Loading State Announcement */}
            {isSubmitting && (
              <div className="sr-only" aria-live="assertive" aria-atomic="true">
                Submitting your message, please wait...
              </div>
            )}
            
            {/* Success State Announcement */}
            {isSuccess && (
              <div className="sr-only" aria-live="assertive" aria-atomic="true">
                Success! Your message has been sent. We'll get back to you soon.
              </div>
            )}
            
            <form 
              onSubmit={handleSubmit} 
              className="space-y-5 sm:space-y-6" 
              noValidate
              aria-label="Contact form"
            >
              {/* Submit Error Message */}
              {submitError && (
                <div 
                  className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3"
                  role="alert"
                  aria-live="assertive"
                >
                  <Icon name="ExclamationCircleIcon" size={20} className="text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">{submitError}</p>
                </div>
              )}

              {/* Name */}
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-foreground mb-2">
                  Name <span className="text-red-500" aria-label="required">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={`w-full border rounded-xl px-4 py-3 sm:py-4 text-base focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all touch-manipulation min-h-[48px] ${
                    errors.name ? 'border-red-500 bg-red-50' : 'border-gray-300'
                  }`}
                  placeholder="Your name"
                  required
                  aria-required="true"
                  aria-invalid={!!errors.name}
                  aria-describedby={errors.name ? 'name-error' : undefined}
                />
                {errors.name && (
                  <p id="name-error" className="mt-2 text-sm text-red-600 flex items-center gap-1" role="alert">
                    <Icon name="ExclamationCircleIcon" size={16} />
                    {errors.name}
                  </p>
                )}
              </div>

              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-foreground mb-2">
                  Email Address <span className="text-red-500" aria-label="required">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={`w-full border rounded-xl px-4 py-3 sm:py-4 text-base focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all touch-manipulation min-h-[48px] ${
                    errors.email ? 'border-red-500 bg-red-50' : 'border-gray-300'
                  }`}
                  placeholder="your@email.com"
                  required
                  aria-required="true"
                  aria-invalid={!!errors.email}
                  aria-describedby={errors.email ? 'email-error' : undefined}
                />
                {errors.email && (
                  <p id="email-error" className="mt-2 text-sm text-red-600 flex items-center gap-1" role="alert">
                    <Icon name="ExclamationCircleIcon" size={16} />
                    {errors.email}
                  </p>
                )}
              </div>

              {/* Message */}
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-foreground mb-2">
                  Message <span className="text-red-500" aria-label="required">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  rows={6}
                  className={`w-full border rounded-xl px-4 py-3 sm:py-4 text-base focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all resize-none touch-manipulation ${
                    errors.message ? 'border-red-500 bg-red-50' : 'border-gray-300'
                  }`}
                  placeholder="How can we help you?"
                  required
                  aria-required="true"
                  aria-invalid={!!errors.message}
                  aria-describedby={errors.message ? 'message-error' : undefined}
                />
                {errors.message && (
                  <p id="message-error" className="mt-2 text-sm text-red-600 flex items-center gap-1" role="alert">
                    <Icon name="ExclamationCircleIcon" size={16} />
                    {errors.message}
                  </p>
                )}
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground font-semibold rounded-full px-8 py-4 sm:py-5 hover:bg-primary/90 transition-all disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation min-h-[56px] text-base sm:text-lg focus:outline-none focus:ring-4 focus:ring-primary/30"
                aria-label={isSubmitting ? 'Sending message' : 'Send message'}
              >
                {isSubmitting ? (
                  <>
                    <Icon name="ArrowPathIcon" size={20} className="animate-spin" aria-hidden="true" />
                    <span>Sending...</span>
                  </>
                ) : (
                  <>
                    <span>Send Message</span>
                    <Icon name="PaperAirplaneIcon" size={20} aria-hidden="true" />
                  </>
                )}
              </button>
            </form>
          </FormErrorBoundary>
        </div>

        {/* Map */}
        <div id="map" className="reveal mt-12 sm:mt-16">
          <div className="container-card p-6 sm:p-8">
            <h3 className="text-xl sm:text-2xl font-bold text-foreground mb-6 text-center">
              Our Location
            </h3>
            <div className="h-64 sm:h-96 rounded-xl overflow-hidden">
              <iframe
                title="Containers by Take a Zip location - Florida, USA"
                src="https://www.google.com/maps?q=Florida,USA&output=embed"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                loading="lazy"
                allowFullScreen
                referrerPolicy="no-referrer-when-downgrade"
                className="w-full h-full"
              />
            </div>
            <p className="mt-4 text-center text-sm sm:text-base text-muted-foreground flex items-center justify-center gap-2">
              <Icon name="MapPinIcon" size={18} className="text-primary" />
              Florida, USA &middot; Nationwide delivery available
            </p>
          </div>
        </div>
      </div>

      {/* Toast Notification */}
      <Toast
        message="Message sent successfully! We'll get back to you within 24 hours."
        type="success"
        isVisible={showToast}
        onClose={() => setShowToast(false)}
      />

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title="Message Sent Successfully!"
        message="Thank you for reaching out. We've received your message and will respond within 24 hours."
        details={submittedData ? [
          { label: 'Name', value: submittedData.name },
          { label: 'Email', value: submittedData.email },
          { label: 'Message Length', value: `${submittedData.message.length} characters` },
        ] : []}
        confirmationNumber={`MSG-${Date.now().toString().slice(-8)}`}
      />
    </section>
  );
};

export default ContactFormSection;